import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "./api.js";
import { RouteProvider, useRoute, navigate, Link } from "./router.jsx";
import { Avatar, Loading, ErrorPanel, ToastProvider, useAsync } from "./ui.jsx";
import { titleCase } from "./format.js";
import { FlowWordmark } from "./components/Logo.jsx";
import { readAuthToken, restoreSession, signOut } from "./identity.js";

import Dashboard from "./pages/Dashboard.jsx";
import Clients from "./pages/Clients.jsx";
import ClientDetail from "./pages/ClientDetail.jsx";
import Invoices from "./pages/Invoices.jsx";
import InvoiceDetail from "./pages/InvoiceDetail.jsx";
import InvoiceEditor from "./pages/InvoiceEditor.jsx";
import Estimates from "./pages/Estimates.jsx";
import EstimateDetail from "./pages/EstimateDetail.jsx";
import Recurring from "./pages/Recurring.jsx";
import Payments from "./pages/Payments.jsx";
import Time from "./pages/Time.jsx";
import Projects from "./pages/Projects.jsx";
import ProjectDetail from "./pages/ProjectDetail.jsx";
import Expenses from "./pages/Expenses.jsx";
import Bills from "./pages/Bills.jsx";
import Items from "./pages/Items.jsx";
import Team from "./pages/Team.jsx";
import Reports from "./pages/Reports.jsx";
import Settings from "./pages/Settings.jsx";
import Onboarding from "./pages/Onboarding.jsx";
import Gate from "./pages/Gate.jsx";
import PublicDocument from "./pages/PublicDocument.jsx";
import McpConnect from "./pages/McpConnect.jsx";

/* ---------------------------------------------------------------- app data */

export const AppCtx = createContext(null);
export const useApp = () => useContext(AppCtx);

/* ---------------------------------------------------------------- navigation */

const NAV = [
  {
    group: "Overview",
    items: [
      { to: "/", label: "Dashboard", icon: "◎", perm: "dashboard.view", match: [""] },
      { to: "/clients", label: "Clients", icon: "◍", perm: "clients.view", count: "active_clients" },
    ],
  },
  {
    group: "Get paid",
    items: [
      { to: "/invoices", label: "Invoices", icon: "▤", perm: "invoices.view", count: "open_invoices" },
      { to: "/estimates", label: "Estimates", icon: "▢", perm: "estimates.view", count: "open_estimates" },
      { to: "/recurring", label: "Recurring", icon: "↻", perm: "recurring.view" },
      { to: "/payments", label: "Payments", icon: "◈", perm: "payments.view" },
    ],
  },
  {
    group: "Track work",
    items: [
      { to: "/time", label: "Time tracking", icon: "◷", perm: "time.view" },
      { to: "/projects", label: "Projects", icon: "❏", perm: "projects.view", count: "active_projects" },
      { to: "/expenses", label: "Expenses", icon: "◰", perm: "expenses.view" },
      { to: "/bills", label: "Contractor bills", icon: "▥", perm: "bills.view", count: "open_bills" },
    ],
  },
  {
    group: "Manage",
    items: [
      { to: "/reports", label: "Reports", icon: "◱", perm: "reports.view" },
      { to: "/items", label: "Items & services", icon: "⛁", perm: "items.view" },
      { to: "/team", label: "Team", icon: "◐", perm: "team.manage", count: "team_members" },
      { to: "/settings", label: "Settings", icon: "⚙", perm: "settings.manage" },
    ],
  },
];

/**
 * The one nav, drawn once. Below 900px the same element becomes an off-canvas
 * drawer (see `.side` in styles.css) rather than a second mobile-only menu —
 * two nav implementations would drift apart the first time an item is added.
 *
 * `onNavigate` closes the drawer on tap. On desktop nothing passes it, so the
 * links behave exactly as before.
 */
function Sidebar({ onNavigate }) {
  const { me, counts } = useApp();
  const { segs } = useRoute();
  const root = segs[0] || "";

  return (
    <aside className="side">
      {/* Wordmark only — no wave. Charcoal surface, so it takes the on-dark pair. */}
      <div className="brand">
        <div>
          <FlowWordmark height={19} tone="dark" />
          <div className="sub">Invoicing</div>
        </div>
        {/* Drawer-only dismiss. Hidden on desktop, where there is nothing to close. */}
        <button className="drawer-x" onClick={onNavigate} aria-label="Close menu">
          ×
        </button>
      </div>

      {NAV.map((group) => {
        const visible = group.items.filter((i) => me.permissions[i.perm]);
        if (!visible.length) return null;
        return (
          <div className="navgrp" key={group.group}>
            <div className="lbl">{group.group}</div>
            {visible.map((item) => {
              const key = item.to.replace(/^\//, "");
              const on = item.match ? item.match.includes(root) : root === key;
              return (
                <Link key={item.to} to={item.to} className={`nav ${on ? "on" : ""}`} onClick={onNavigate}>
                  <span className="ic">{item.icon}</span>
                  <span>{item.label}</span>
                  {item.count && counts[item.count] ? <span className="pill">{counts[item.count]}</span> : null}
                </Link>
              );
            })}
          </div>
        );
      })}

      <div className="who" onClick={signOut} title="Sign out">
        <Avatar name={me.name} size={28} />
        <div>
          <div className="n">{me.name}</div>
          <div className="r">{me.role} · sign out</div>
        </div>
      </div>
    </aside>
  );
}

/**
 * Mobile-only bar. Carries the one thing the phone layout was missing entirely:
 * a way to open the nav. Also shows where you are, since the drawer is closed by
 * default and the page's own <h1> scrolls away.
 */
function MobileBar({ onOpen }) {
  const { me } = useApp();
  const { segs } = useRoute();
  const root = segs[0] || "";

  const label =
    NAV.flatMap((g) => g.items).find((i) => (i.match ? i.match.includes(root) : i.to.replace(/^\//, "") === root))
      ?.label || titleCase(root);

  return (
    <div className="mbar">
      <button className="burger" onClick={onOpen} aria-label="Open menu" aria-expanded="false">
        <span />
        <span />
        <span />
      </button>
      <div className="mbar-title">{label}</div>
      <div className="grow" />
      <Avatar name={me.name} size={28} />
    </div>
  );
}

/* ---------------------------------------------------------------- routing */

function CurrentPage() {
  const { segs } = useRoute();
  const { me } = useApp();
  const [root, a, b] = segs;

  const guard = (perm, node) =>
    me.permissions[perm] ? node : <Denied />;

  switch (root) {
    case undefined:
      return me.permissions["dashboard.view"] ? <Dashboard /> : <Time />;
    case "clients":
      return guard("clients.view", a ? <ClientDetail id={a} /> : <Clients />);
    case "invoices":
      if (a === "new") return guard("invoices.manage", <InvoiceEditor />);
      if (a && b === "edit") return guard("invoices.manage", <InvoiceEditor id={a} />);
      return guard("invoices.view", a ? <InvoiceDetail id={a} /> : <Invoices />);
    case "estimates":
      return guard("estimates.view", a ? <EstimateDetail id={a} /> : <Estimates />);
    case "recurring":
      return guard("recurring.view", <Recurring />);
    case "payments":
      return guard("payments.view", <Payments />);
    case "time":
      return guard("time.view", <Time />);
    case "projects":
      return guard("projects.view", a ? <ProjectDetail id={a} /> : <Projects />);
    case "expenses":
      return guard("expenses.view", <Expenses />);
    case "bills":
      return guard("bills.view", <Bills />);
    case "items":
      return guard("items.view", <Items />);
    case "team":
      return guard("team.manage", <Team />);
    case "reports":
      return guard("reports.view", <Reports />);
    case "settings":
      return guard("settings.manage", <Settings />);
    default:
      return (
        <div className="wrap">
          <div className="card">
            <div className="empty">
              <div className="big">◌</div>
              That page doesn't exist.{" "}
              <span className="linky" onClick={() => navigate("/")}>
                Back to the dashboard
              </span>
            </div>
          </div>
        </div>
      );
  }
}

const Denied = () => (
  <div className="wrap">
    <div className="card">
      <div className="empty">
        <div className="big">⊘</div>
        Your role doesn't have access to this area.
      </div>
    </div>
  </div>
);

/* ---------------------------------------------------------------- auth */

/**
 * We talk to Netlify Identity through GoTrue directly rather than the widget, so
 * the sign-in, invitation and password-reset screens are ours — see Gate.jsx for
 * why that matters.
 */
function useIdentity() {
  const [user, setUser] = useState(undefined); // undefined while restoring

  useEffect(() => {
    let alive = true;
    // A URL carrying an invite or recovery token must reach Gate, not be
    // short-circuited by a stale stored session.
    if (readAuthToken({ hash: window.location.hash, search: window.location.search })) {
      setUser(null);
      return;
    }
    restoreSession()
      .then((u) => alive && setUser(u || null))
      .catch(() => alive && setUser(null));
    return () => {
      alive = false;
    };
  }, []);

  return { user, setUser };
}

/* ---------------------------------------------------------------- shell */

function Shell() {
  const boot = useAsync(() => api.get("bootstrap"), []);

  if (boot.loading) return <Loading label="Loading your books…" />;
  if (boot.error)
    return (
      <div className="wrap" style={{ maxWidth: 620, margin: "60px auto" }}>
        <ErrorPanel error={boot.error} onRetry={boot.reload} />
        <p className="sm dim">
          If this is the first run, check that the database migrations have been applied — they run
          automatically on deploy.
        </p>
      </div>
    );

  const value = { ...boot.data, reloadApp: boot.reload };

  // Signed in, but no business yet: there is nothing to scope a query to, so
  // there is no sidebar to draw either. Onboarding calls reloadApp when the
  // organisation exists, and this same bootstrap comes back with the real app.
  if (boot.data?.needs_onboarding)
    return (
      <AppCtx.Provider value={value}>
        <Onboarding />
      </AppCtx.Provider>
    );

  return (
    <AppCtx.Provider value={value}>
      <AppFrame />
    </AppCtx.Provider>
  );
}

/**
 * Split out of Shell so it can use hooks that depend on AppCtx (MobileBar reads
 * `me`), and so the drawer's open state has somewhere to live that doesn't
 * re-run the bootstrap fetch.
 */
function AppFrame() {
  const [drawer, setDrawer] = useState(false);
  const { path } = useRoute();
  const close = useCallback(() => setDrawer(false), []);

  // Any route change closes the drawer. Covers the paths that don't go through a
  // nav <Link> — a row click, a breadcrumb, the browser back button.
  useEffect(close, [path, close]);

  useEffect(() => {
    if (!drawer) return;
    const onKey = (e) => e.key === "Escape" && close();
    window.addEventListener("keydown", onKey);
    // Stop the page behind the drawer scrolling under the finger.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [drawer, close]);

  return (
    <div className={`app ${drawer ? "drawer-open" : ""}`}>
      <Sidebar onNavigate={close} />
      {/* Tapping beside the drawer closes it. Inert at desktop widths. */}
      <div className="drawer-mask" onClick={close} />
      <div className="main">
        <MobileBar onOpen={() => setDrawer(true)} />
        <CurrentPage />
      </div>
    </div>
  );
}

export default function App() {
  return (
    <ToastProvider>
      <RouteProvider>
        <Root />
      </RouteProvider>
    </ToastProvider>
  );
}

function Root() {
  const { segs } = useRoute();
  const { user, setUser } = useIdentity();

  // Client-facing pages are public and must never wait on Identity.
  if (segs[0] === "pay") return <PublicDocument kind={segs[1]} token={segs[2]} />;
  if (segs[0] === "connect" && segs[1] === "flow-books")
    return <McpConnect user={user} onSignedIn={setUser} />;

  if (user === undefined) return <Loading label="Checking your session…" />;
  if (!user) return <Gate onSignedIn={setUser} />;
  return <Shell />;
}
